// Merging one block of text into a file somebody else owns.
//
// `CLAUDE.md` belongs to the project, not to this CLI. It will hold a team's own instructions, and it will
// be edited by hand between runs — so the segment cannot simply be written, and it cannot be appended twice.
// Markers give the CLI a region it owns inside a file it does not: everything between them is replaced on
// every run, everything outside them is never touched.

// The exact strings matter: projects generated before this command carry these markers already, so matching
// them is what makes `init-claude` *replace* an existing block there rather than append a second one.
export const BEGIN_MARKER = '<!-- flowrunner-cli:ai:start -->'
export const END_MARKER = '<!-- flowrunner-cli:ai:end -->'

/**
 * What happened to the file.
 *
 * `inserted` and `replaced` are kept apart because they answer different questions: the first says the CLI
 * added something to a file that was already there, the second says it refreshed what it had added before.
 */
export type SegmentOutcome = 'created' | 'inserted' | 'replaced' | 'unchanged'

export interface MergeResult {
  contents: string
  outcome: SegmentOutcome
}

/** The owned region, marker lines included. */
function block(segment: string): string {
  return `${ BEGIN_MARKER }\n${ segment.trimEnd() }\n${ END_MARKER }\n`
}

/**
 * Compute the new contents of an instructions file with `segment` merged into it.
 *
 * Pure — takes the current contents (`null` when the file does not exist) and returns the new ones, so the
 * decision of what to write is testable without a filesystem, and the caller stays the only thing that
 * touches disk.
 *
 * An unbalanced marker pair throws. A half-marked file is either a bad hand-edit or a merge conflict
 * someone resolved wrongly, and both readings of "repair it" — treat the file as unmarked and append a
 * second block, or guess where the missing marker went — silently damage the user's own instructions.
 */
export function mergeSegment(current: string | null, segment: string, file: string): MergeResult {
  const marked = block(segment)

  if (current === null) {
    return { contents: marked, outcome: 'created' }
  }

  const begin = current.indexOf(BEGIN_MARKER)
  const end = current.indexOf(END_MARKER)

  if (begin === -1 && end === -1) {
    // A blank line between their content and ours, and never a second one.
    const separator = current.length === 0 ? '' : current.endsWith('\n\n') ? '' : current.endsWith('\n') ? '\n' : '\n\n'

    return { contents: `${ current }${ separator }${ marked }`, outcome: 'inserted' }
  }

  if (begin === -1 || end === -1 || end < begin) {
    throw new Error(
      `${ file } has an unbalanced flowrunner-cli marker pair. ` +
      `Restore both "${ BEGIN_MARKER }" and "${ END_MARKER }", or delete them and run this again.`
    )
  }

  const before = current.slice(0, begin)
  const after = current.slice(end + END_MARKER.length).replace(/^\n/, '')
  const contents = `${ before }${ marked }${ after }`

  return { contents, outcome: contents === current ? 'unchanged' : 'replaced' }
}
